import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslations } from '@altermaind/core/i18n';
import { AnimatedShape, Box, Typography } from '@altermaind/web/ui';
import { ClaimsComponent } from '@/_shared/components/Claims';
import { Slider } from '@/_shared/components/Slider';
import { Roles } from '@/_shared/constants/roles';
import { Routes } from '@/_shared/constants/routes';
import { ContentType } from '@/swagger/constants/contents-orchestrator-enums';
import { getDashboardContentsCall } from '../../state';
import { ContentsSkeleton } from '../ContentsSkeleton';
import { ContentToCard } from '../ContentToCard';
import { EmptyState } from '../EmptyState';
import { FilterContents } from '../FilterContents/FilterContents';

export const MainContents: FC = () => {
  const t = useTranslations('trainingSoftSkills.sections.mainContents');
  const tShared = useTranslations();
  const navigate = useNavigate();
  const {
    error: errorGetDashboardContents,
    execute: executeGetDashboardContents,
    loading: loadingGetDashboardContents,
    dashboardVideos,
    dashboardQuizzes,
    dashboardPdfs,
    fullRequest: { payload },
  } = getDashboardContentsCall.hook();

  const noContentsAvailable = useMemo(() => {
    if (errorGetDashboardContents) {
      return true;
    }

    if (!loadingGetDashboardContents && !(payload?.search || payload.tag)) {
      return !dashboardVideos?.length && !dashboardPdfs?.length && !dashboardQuizzes?.length;
    }
    return false;
  }, [
    dashboardPdfs?.length,
    dashboardQuizzes?.length,
    dashboardVideos?.length,
    errorGetDashboardContents,
    loadingGetDashboardContents,
    payload?.search,
    payload?.tag,
  ]);

  const noFilteredContentsAvailable = useMemo(() => {
    if (!loadingGetDashboardContents && (payload?.search || payload.tag)) {
      return !dashboardVideos?.length && !dashboardPdfs?.length && !dashboardQuizzes?.length;
    }
    return false;
  }, [
    dashboardPdfs?.length,
    dashboardQuizzes?.length,
    dashboardVideos?.length,
    loadingGetDashboardContents,
    payload?.search,
    payload.tag,
  ]);

  const onGoToListingPage = useCallback(
    (contentType: `${ContentType}`) => {
      navigate(
        Routes.TrainingSoftSkillsSpecificContents.replace(
          ':contentType',
          contentType.toLowerCase(),
        ),
      );
    },
    [navigate],
  );

  useEffect(() => {
    executeGetDashboardContents({
      length: 10,
    });
  }, [executeGetDashboardContents]);

  if (noContentsAvailable) {
    return <EmptyState />;
  }

  return (
    <Box mt='m'>
      <FilterContents
        title={t('title')}
        titleProps={{
          variant: 'h2',
        }}
        call={getDashboardContentsCall}
        showFiltersComponent={false}
      />

      {loadingGetDashboardContents ? (
        <>
          <AnimatedShape width='30%' height={25} mb='s' />
          <ContentsSkeleton halfHeightLayout={false} type='video' />
          <AnimatedShape width='30%' height={25} mb='s' mt='xxl' />
          <ContentsSkeleton halfHeightLayout={false} type='document' />
          <AnimatedShape width='30%' height={25} mb='s' mt='xxl' />
          <AnimatedShape width='80%' height={10} mb='xxs' />
          <AnimatedShape width='80%' height={10} mb='xxs' />
          <AnimatedShape width='20%' height={10} mb='s' />
          <ContentsSkeleton
            halfHeightLayout
            type='quiz'
            slideToShow={{
              desktopLarge: 3.5,
              desktop: 2.5,
              tablet: 1.5,
              mobile: 1,
            }}
          />
        </>
      ) : null}
      {noFilteredContentsAvailable ? (
        <Typography
          textAlign='center'
          variant='p2/light'
          mt={'250px' as never}
          mb={'250px' as never}
        >
          {tShared('table.noContent')}
        </Typography>
      ) : null}
      {!loadingGetDashboardContents && dashboardVideos && dashboardVideos?.length ? (
        <Box mb='l'>
          <Slider.SliderBottomRightArrow
            title={t('titleVideo')}
            onGoToListingPage={{
              onClick: () => onGoToListingPage(ContentType.VIDEO),
              text: t('showAll'),
            }}
          >
            {dashboardVideos?.map(content => (
              <ContentToCard content={content} key={content.id} />
            ))}
          </Slider.SliderBottomRightArrow>
        </Box>
      ) : null}
      {!loadingGetDashboardContents && dashboardPdfs && dashboardPdfs?.length ? (
        <Box mb='l'>
          <Slider.SliderBottomRightArrow
            title={t('titleDocuments')}
            onGoToListingPage={{
              onClick: () => onGoToListingPage(ContentType.PDF),
              text: t('showAll'),
            }}
          >
            {dashboardPdfs?.map(content => (
              <ContentToCard content={content} key={content.id} />
            ))}
          </Slider.SliderBottomRightArrow>
        </Box>
      ) : null}
      {!loadingGetDashboardContents && dashboardQuizzes && dashboardQuizzes?.length ? (
        <Box mb='l'>
          <ClaimsComponent roles={[Roles.Admin, Roles.HR_Delegate]}>
            <Slider.SliderBottomRightArrow
              title={t('titleQuizzes.admin')}
              slideToShow={{
                desktopLarge: 3.5,
                desktop: 2.5,
                tablet: 1.5,
                mobile: 1,
              }}
              onGoToListingPage={{
                onClick: () => onGoToListingPage(ContentType.QUIZ),
                text: t('showAll'),
              }}
            >
              {dashboardQuizzes?.map(content => (
                <ContentToCard content={content} key={content.id} halfHeightLayout />
              ))}
            </Slider.SliderBottomRightArrow>
          </ClaimsComponent>
          <ClaimsComponent roles={[Roles.Employee, Roles.Manager]}>
            <Slider.SliderBottomRightArrow
              title={t('titleQuizzes.learning')}
              description={t('descriptionQuizzes')}
              slideToShow={{
                desktopLarge: 3.5,
                desktop: 2.5,
                tablet: 1.5,
                mobile: 1,
              }}
              onGoToListingPage={{
                onClick: () => onGoToListingPage(ContentType.QUIZ),
                text: t('showAll'),
              }}
            >
              {dashboardQuizzes?.map(content => (
                <ContentToCard content={content} key={content.id} halfHeightLayout />
              ))}
            </Slider.SliderBottomRightArrow>
          </ClaimsComponent>
        </Box>
      ) : null}
    </Box>
  );
};
